import { useControlesObra, useIniciarRegistro } from '@/hooks/useCalidad';
import { esSoloLectura } from '@/lib/cliente';
import { cn } from '@/lib/utils';
import type { ControlSubPartida, EstadoInspeccion } from '@adelante/shared';
import { ClipboardText, Lock, SealCheck, SealWarning, Warning } from '@phosphor-icons/react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Línea de calidad bajo una sub-partida en la captura de avance:
 * chip de inspección (bloquea el inicio) + chip de evaluación + NCs abiertas.
 */

const INSPECCION: Record<EstadoInspeccion, { label: string; clase: string }> = {
  pendiente: {
    label: 'Inspección pendiente',
    clase: 'border-amber-300 bg-amber-50 text-amber-800',
  },
  en_curso: { label: 'Inspección en curso', clase: 'border-blue-300 bg-blue-50 text-blue-800' },
  rechazada: { label: 'Inspección rechazada', clase: 'border-red-300 bg-red-50 text-red-700' },
  aprobada: {
    label: 'Inspección aprobada',
    clase: 'border-adelante-primary bg-adelante-primary/10 text-adelante-primary-dark',
  },
  liberada: {
    label: 'Liberada (excepción)',
    clase: 'border-orange-300 bg-orange-50 text-orange-800',
  },
};

/**
 * Controles de calidad de una obra + acción para abrir la ficha de una
 * sub-partida (reutiliza el registro vigente o inicia uno nuevo; si la
 * inspección fue rechazada, inicia una reinspección).
 * `codigo` undefined = no consulta (p. ej. tarjeta del Kanban colapsada).
 */
export function useControlesCalidad(codigo: string | undefined) {
  const q = useControlesObra(codigo);
  const iniciar = useIniciarRegistro();
  const navigate = useNavigate();

  const controles = useMemo(() => {
    const m = new Map<number, ControlSubPartida>();
    for (const c of q.data?.sub_partidas ?? []) m.set(c.sub_partida_id, c);
    return m;
  }, [q.data]);

  async function abrir(subPartidaId: number, tipo: 'inspeccion' | 'evaluacion') {
    const c = controles.get(subPartidaId);
    const info = tipo === 'inspeccion' ? c?.inspeccion : c?.evaluacion;
    if (!c || !info || !codigo) return;
    const reutilizar =
      info.registro_id && !(tipo === 'inspeccion' && c.inspeccion?.estado === 'rechazada');
    if (reutilizar) {
      navigate(`/campo/calidad/${info.registro_id}`);
      return;
    }
    if (esSoloLectura()) return;
    try {
      const r = await iniciar.mutateAsync({
        ficha_id: info.ficha_id,
        obra_codigo: codigo,
        sub_partida_id: subPartidaId,
      });
      navigate(`/campo/calidad/${r.id}`);
    } catch (e) {
      toast.error('No se pudo abrir la ficha', {
        description: e instanceof Error ? e.message : 'Error desconocido',
      });
    }
  }

  return { controles, abrir, abriendo: iniciar.isPending, refetch: q.refetch };
}

/**
 * Qué impide la inspección de calidad en este momento (misma regla que el backend):
 *   inicio    → no se puede pasar de 0% a >0%
 *   completar → no se puede llegar a 100%
 */
export interface BloqueoCalidad {
  inicio: boolean;
  completar: boolean;
}

export function bloqueoCalidad(
  control: ControlSubPartida | undefined,
  pctActual: number,
): BloqueoCalidad {
  const insp = control?.inspeccion;
  if (
    !insp ||
    insp.estado === 'aprobada' ||
    insp.estado === 'liberada' ||
    insp.bloqueo === 'ninguno'
  )
    return { inicio: false, completar: false };
  if (insp.bloqueo === 'inicio') {
    const b = pctActual <= 0;
    return { inicio: b, completar: b };
  }
  return { inicio: false, completar: pctActual < 100 };
}

/** ¿Se permite fijar este % con el bloqueo actual? */
export function permiteAvance(b: BloqueoCalidad, pctNuevo: number): boolean {
  return !(b.inicio && pctNuevo > 0) && !(b.completar && pctNuevo >= 100);
}

interface Props {
  control: ControlSubPartida;
  bloqueo: BloqueoCalidad;
  disabled: boolean;
  onAbrir: (tipo: 'inspeccion' | 'evaluacion') => void;
}

export function ControlCalidadFila({ control, bloqueo, disabled, onAbrir }: Props) {
  const insp = control.inspeccion;
  const ev = control.evaluacion;
  const evLabel = !ev
    ? ''
    : ev.estado === 'cerrado'
      ? ev.resultado === 'aprobado'
        ? 'Evaluación OK'
        : 'Evaluación con observaciones'
      : ev.estado === 'borrador'
        ? 'Evaluación en curso'
        : 'Evaluar';

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-dashed border-border pt-2">
      {(bloqueo.inicio || bloqueo.completar) && (
        <span
          className="inline-flex items-center gap-1 text-[11px] font-medium text-red-700"
          title={
            bloqueo.inicio
              ? 'No se puede iniciar sin la inspección aprobada'
              : 'Se puede avanzar, pero no completar (100%) sin la inspección aprobada'
          }
        >
          <Lock className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
          {bloqueo.inicio ? 'Bloqueada' : 'No se puede completar'}
        </span>
      )}
      {insp && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => onAbrir('inspeccion')}
          className={cn(
            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium disabled:opacity-50',
            INSPECCION[insp.estado].clase,
          )}
          title={insp.ficha_nombre}
        >
          {insp.estado === 'aprobada' ? (
            <SealCheck className="h-3.5 w-3.5" weight="fill" aria-hidden="true" />
          ) : (
            <ClipboardText className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
          )}
          {INSPECCION[insp.estado].label}
          {insp.intento > 1 && (
            <span className="font-normal opacity-80">· reinsp. {insp.intento - 1}</span>
          )}
        </button>
      )}
      {ev && (
        <button
          type="button"
          disabled={disabled || (!ev.habilitada && !ev.registro_id)}
          onClick={() => onAbrir('evaluacion')}
          className={cn(
            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium disabled:opacity-50',
            ev.estado === 'cerrado' && ev.resultado === 'aprobado'
              ? 'border-adelante-primary bg-adelante-primary/10 text-adelante-primary-dark'
              : ev.estado === 'cerrado'
                ? 'border-amber-300 bg-amber-50 text-amber-800'
                : 'border-border bg-white text-foreground',
          )}
          title={
            ev.habilitada || ev.registro_id
              ? ev.ficha_nombre
              : 'La evaluación se habilita cuando la sub-partida tiene avance'
          }
        >
          {ev.estado === 'cerrado' && ev.resultado !== 'aprobado' ? (
            <SealWarning className="h-3.5 w-3.5" weight="fill" aria-hidden="true" />
          ) : (
            <ClipboardText className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
          )}
          {evLabel}
        </button>
      )}
      {control.nc_abiertas > 0 && (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-700">
          <Warning className="h-3.5 w-3.5" weight="fill" aria-hidden="true" />
          {control.nc_abiertas} NC abierta{control.nc_abiertas === 1 ? '' : 's'}
        </span>
      )}
    </div>
  );
}
